import type { TemplateField } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { FileSpreadsheet, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { columnFor } from '../../../import/columns';

/** Just enough of a column to show it — either vocabulary satisfies this. */
interface DescribedColumn {
  example: string;
}

/**
 * Choosing the file.
 *
 * Ordered by what the operator is here to do: which template is reading the file, then the file
 * itself, then the columns that template expects. The column list is reference material and runs
 * to a dozen rows — above the drop zone it pushes the only control on the step off the screen.
 */
export function FileStep({
  templateName,
  fields,
  fieldKeys,
  file,
  error,
  onFile,
  onClearFile,
  onChangeTemplate,
  describeColumn = columnFor,
}: {
  templateName: string;
  fields: readonly TemplateField[];
  fieldKeys: readonly string[];
  file: { name: string; lines: number } | null;
  error: string | null;
  onFile: (file: File) => void;
  onClearFile: () => void;
  /**
   * The vocabulary this file is read against. Defaults to the tournament importer's; the results
   * importer passes its own, so the examples shown are the ones its columns actually take.
   */
  describeColumn?: (fieldKey: string) => DescribedColumn | null;
  /** Null when this room has only one import template, so there is nothing to change to. */
  onChangeTemplate: (() => void) | null;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const labels = new Map(fields.map((f) => [f.key, f.label]));
  const pick = () => input.current?.click();

  return (
    <div className="space-y-5">
      {/* The template decides how every cell is read, so which one is in use is not a detail to
          leave implied — least of all when the step was reached without passing the picker. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-ink-800/10 bg-felt-50/40 px-4 py-3">
        <div className="min-w-0">
          <span className="block text-[11px] uppercase tracking-wide text-ink-800/50">
            Reading with
          </span>
          <span className="block truncate text-sm font-medium text-ink-950">{templateName}</span>
        </div>
        {onChangeTemplate ? (
          <button
            type="button"
            onClick={onChangeTemplate}
            className="shrink-0 text-xs font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
          >
            Change template
          </button>
        ) : null}
      </div>

      {file ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-felt-500/40 bg-felt-50/50 p-4">
          <FileSpreadsheet className="size-5 shrink-0 text-felt-700" />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-ink-950">{file.name}</span>
            <span className="num block text-xs text-ink-800/60">
              {file.lines} line{file.lines === 1 ? '' : 's'} read
            </span>
          </div>
          <button
            type="button"
            onClick={pick}
            className="shrink-0 rounded-lg border border-ink-800/15 px-3 py-1.5 text-xs text-ink-800 hover:bg-white"
          >
            Choose another
          </button>
          <button
            type="button"
            onClick={onClearFile}
            aria-label={`Remove ${file.name}`}
            className="shrink-0 p-1 text-ink-800/40 hover:text-chip-500"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const dropped = e.dataTransfer.files[0];
            if (dropped) onFile(dropped);
          }}
          className={`rounded-lg border-2 border-dashed p-8 text-center transition ${
            over ? 'border-felt-500 bg-felt-50/50' : 'border-ink-800/15'
          }`}
        >
          <Upload className="mx-auto size-6 text-ink-800/30" />
          <p className="mt-2 text-sm text-ink-800/70">Drop a CSV file here, or</p>
          <button
            type="button"
            onClick={pick}
            className="mt-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-felt-50"
          >
            Choose a file
          </button>
          <p className="mt-3 text-xs text-ink-800/45">
            CSV only. Nothing is created until you say so.
          </p>
        </div>
      )}

      <ErrorNotice message={error} />

      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800/50">
          Columns {templateName} expects, in this order
        </h4>
        <ol className="mt-2 divide-y divide-ink-800/5 rounded-lg border border-ink-800/10">
          {fieldKeys.map((key, index) => {
            const column = describeColumn(key);
            return (
              <li key={key} className="flex items-baseline gap-3 px-3 py-2">
                <span className="num w-5 shrink-0 text-xs text-ink-800/40">{index + 1}</span>
                <span className="min-w-0 flex-1 text-sm text-ink-950">
                  {labels.get(key) ?? key}
                  {column ? (
                    <span className="ml-2 font-mono text-[11px] text-ink-800/45">
                      {column.example}
                    </span>
                  ) : (
                    // Said here as well as in the review, because this is where an operator can
                    // still do something about it without spending a file.
                    <span className="ml-2 text-[11px] text-ink-800/45">
                      not read by the importer
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      {/* Outside both branches: the same input serves the drop zone and "Choose another", and
          remounting it on that swap would drop the click that opened it. */}
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          if (chosen) onFile(chosen);
          // Cleared so choosing the same file twice still fires a change — after a failed read,
          // picking the same corrected file is the obvious next move.
          e.target.value = '';
        }}
      />
    </div>
  );
}
